import { DateTime, Interval } from 'luxon';
import check from './check-types-ext';
import { ShopAvailabilityStatus, WeekdayToTimeBoxWeekday, } from '../plugins/commonTypes/ShopTypes';
import { parseWildcardDates } from '../helpers/dateHelpers';
import { intersectAllRules } from '../orders/OrderHelperObject';
export const ALL_DATES = '*-*-*';
export const getCurrentTimeInTZ = (timeZone) => {
    return DateTime.local({ zone: timeZone });
};
export const formatTime = (v) => `0${v}`.slice(-2, 2);
export const getDefaultRule = ({ shopData, tenantData, userTimeInShopTZ, }) => {
    const stringDate = userTimeInShopTZ.toFormat('yyyy-MM-dd');
    if (shopData !== null && Array.isArray(shopData.shopWorkingHours)) {
        const shopRule = shopData.shopWorkingHours.find((swh) => swh.date === stringDate && swh.type === 'store');
        if (shopRule) {
            if (shopRule.closed && shopRule.open === null && shopRule.close === null) {
                return null;
            }
            else {
                const shopRuleOpen = shopRule.open.split('T')[1].split(':');
                const shopRuleClose = shopRule.close.split('T')[1].split(':');
                const openWorkingHour = parseInt(shopRuleOpen[0]);
                const openWorkingMinute = parseInt(shopRuleOpen[1]);
                const closeWorkingHour = parseInt(shopRuleClose[0]);
                const closeWorkingMinute = parseInt(shopRuleClose[1]);
                return {
                    id: 999,
                    createdAt: new Date(),
                    createdById: '',
                    createdByName: '',
                    name: '',
                    departmentFilter: null,
                    options: {},
                    isActive: true,
                    isWhitelist: true,
                    updatedAt: new Date(),
                    tenantId: tenantData.tenantId,
                    dateFilter: '*-*-*',
                    weekDayFilter: null,
                    shopFilter: null,
                    productFilter: null,
                    pickupRanges: [
                        {
                            pickupRangeFrom: `${openWorkingHour}:${openWorkingMinute}:00`,
                            pickupRangeTo: `${closeWorkingHour}:${closeWorkingMinute}:00`,
                        },
                    ],
                };
            }
        }
    }
    return {
        id: 999,
        createdAt: new Date(),
        createdById: '',
        createdByName: '',
        name: '',
        departmentFilter: null,
        options: {},
        isActive: true,
        isWhitelist: true,
        updatedAt: new Date(),
        tenantId: tenantData.tenantId,
        dateFilter: '*-*-*',
        weekDayFilter: null,
        shopFilter: null,
        productFilter: null,
        pickupRanges: [
            {
                pickupRangeFrom: `${formatTime(tenantData.defaultPickupStartTime.hour)}:${formatTime(tenantData.defaultPickupStartTime.minute)}:00`,
                pickupRangeTo: tenantData.defaultPickupEndTime.hour > 23
                    ? '23:59:00'
                    : `${formatTime(tenantData.defaultPickupEndTime.hour)}:${formatTime(tenantData.defaultPickupEndTime.minute)}:00`,
            },
        ],
    };
};
export const getDefaultShopWorkingHours = () => {
    const defaultOpeningTime = '00:00';
    const defaultClosingTime = '23:59';
    return {
        openingTime: defaultOpeningTime,
        closingTime: defaultClosingTime,
    };
};
export const getFilterObjectForShopOpeningHours = (userCurTimeInShopTZ, shopOpeningHours) => {
    var _a, _b;
    const dateRule = (_a = shopOpeningHours === null || shopOpeningHours === void 0 ? void 0 : shopOpeningHours.dateRules) === null || _a === void 0 ? void 0 : _a.find((rule) => rule.date === userCurTimeInShopTZ.toFormat('yyyy-MM-dd'));
    if (dateRule) {
        return dateRule;
    }
    return (_b = shopOpeningHours === null || shopOpeningHours === void 0 ? void 0 : shopOpeningHours.weekdayRules) === null || _b === void 0 ? void 0 : _b.find((rule) => rule.weekday === userCurTimeInShopTZ.weekdayLong);
};
export const getTimeRangeApplicableForCurrentUserTime = (ranges, userTimeInShopTZ) => {
    const rangeWithCurrentTime = ranges.filter((range) => range.openingTime.startOf('second') <= userTimeInShopTZ.startOf('second') &&
        range.closingTime.startOf('second') >= userTimeInShopTZ.startOf('second'));
    if (rangeWithCurrentTime.length && rangeWithCurrentTime[0].closingTime && rangeWithCurrentTime[0].openingTime) {
        return {
            openingTime: rangeWithCurrentTime[0].openingTime.toFormat('HH:mm'),
            closingTime: rangeWithCurrentTime[0].closingTime.toFormat('HH:mm'),
        };
    }
    const rangeWithMaxClosingTime = ranges.reduce((acc, cur) => {
        if (!acc || cur.closingTime.startOf('second') > acc.closingTime.startOf('second')) {
            return cur;
        }
        return acc;
    }, undefined);
    if (rangeWithMaxClosingTime &&
        userTimeInShopTZ.startOf('second') >= rangeWithMaxClosingTime.closingTime.startOf('second')) {
        return {
            openingTime: rangeWithMaxClosingTime.openingTime.toFormat('HH:mm'),
            closingTime: rangeWithMaxClosingTime.closingTime.toFormat('HH:mm'),
        };
    }
    const nextOpeningRange = ranges.reduce((acc, cur) => {
        const diff = Interval.fromDateTimes(userTimeInShopTZ, cur.openingTime).length('minutes');
        return !isNaN(diff) && acc.diff > diff
            ? Object.assign(Object.assign({}, cur), { diff }) : acc;
    }, { diff: Number.MAX_SAFE_INTEGER });
    if (nextOpeningRange.closingTime && nextOpeningRange.openingTime) {
        return {
            openingTime: nextOpeningRange.openingTime.toFormat('HH:mm'),
            closingTime: nextOpeningRange.closingTime.toFormat('HH:mm'),
        };
    }
    return {
        openingTime: null,
        closingTime: null,
    };
};
export const getTimeRangeByShopOpeningHours = (userCurrentDateTime, shop) => {
    var _a;
    const userCurTimeInShopTZ = userCurrentDateTime.setZone(shop.shopTimeZone);
    const filterObject = getFilterObjectForShopOpeningHours(userCurTimeInShopTZ, shop.openingHoursByWeekDay);
    if (filterObject) {
        if (filterObject.closed) {
            return {
                openingTime: null,
                closingTime: null,
            };
        }
        const ranges = ((_a = filterObject.openingRanges) === null || _a === void 0 ? void 0 : _a.map((range) => ({
            openingTime: DateTime.fromFormat(range.opens, 'HH:mm:ss', {
                zone: shop.shopTimeZone,
            }),
            closingTime: DateTime.fromFormat(range.closes, 'HH:mm:ss', {
                zone: shop.shopTimeZone,
            }),
        }))) || [];
        return getTimeRangeApplicableForCurrentUserTime(ranges, userCurTimeInShopTZ);
    }
    return {
        openingTime: null,
        closingTime: null,
    };
};
const flatMap = (arr, f) => arr.reduce((x, y) => [...x, ...f(y)], []);
export const isRuleSuitableForCurrentDay = (shopTimeZone, rule, userCurrentTime) => {
    if (rule.dateFilter) {
        if (!Array.isArray(rule.dateFilter) && rule.dateFilter === ALL_DATES) {
            return true;
        }
        if (Array.isArray(rule.dateFilter) && rule.dateFilter.includes(ALL_DATES)) {
            return true;
        }
        if (Array.isArray(rule.dateFilter)) {
            const userCurrentDate = userCurrentTime.setZone(shopTimeZone);
            const userCurrentDateStr = userCurrentDate.toFormat('yyyy-MM-dd');
            const convertedDates = flatMap(rule.dateFilter, (dateStr) => parseWildcardDates(dateStr, userCurrentDate.toJSDate()));
            return convertedDates.some((dateStr) => {
                const shopWorkingDate = DateTime.fromFormat(dateStr, 'yyyy-MM-dd', {
                    zone: shopTimeZone,
                }).toFormat('yyyy-MM-dd');
                return shopWorkingDate === userCurrentDateStr;
            });
        }
    }
    if (rule.weekDayFilter && rule.weekDayFilter.length) {
        return rule.weekDayFilter.split(',').includes(userCurrentTime.weekday.toString());
    }
    return false;
};
export const mergePickupRules = (rules, defaultRule) => {
    const applicableRules = [...(rules.length === 0 ? [defaultRule] : []), ...rules];
    return intersectAllRules(applicableRules);
};
export const getPickupTimeRange = ({ shop, tenantData, pickupRules, userCurrentDateTime, }) => {
    const shopTimeZone = shop.shopTimeZone;
    const userTimeInShopTZ = userCurrentDateTime.setZone(shopTimeZone);
    if (!pickupRules.length) {
        return getDefaultShopWorkingHours();
    }
    const defaultRule = getDefaultRule({
        shopData: shop,
        tenantData,
        userTimeInShopTZ,
    });
    const ranges = mergePickupRules(pickupRules, defaultRule).map((range) => ({
        openingTime: DateTime.fromFormat(range.pickupRangeFrom, 'HH:mm:ss', {
            zone: shopTimeZone,
        }),
        closingTime: DateTime.fromFormat(range.pickupRangeTo, 'HH:mm:ss', {
            zone: shopTimeZone,
        }),
    }));
    return getTimeRangeApplicableForCurrentUserTime(ranges, userTimeInShopTZ);
};
export const getTimeRange = ({ pickupRules, userCurrentDateTime, shop, tenantData, useShopOpeningHours, }) => {
    if (shop.openingHoursByWeekDay && useShopOpeningHours) {
        return getTimeRangeByShopOpeningHours(userCurrentDateTime, shop);
    }
    const filteredPickupRules = pickupRules.filter((rule) => rule.shopFilter.includes(shop.id) &&
        rule.pickupRanges.length &&
        rule.isActive &&
        isRuleSuitableForCurrentDay(shop.shopTimeZone, rule, userCurrentDateTime));
    return getPickupTimeRange({
        pickupRules: filteredPickupRules,
        userCurrentDateTime,
        tenantData,
        shop,
    });
};
export const isOpeningLaterToday = ({ openingTime, userCurrentDateTime, shopTimeZone, }) => {
    const userTimeInShopTZ = userCurrentDateTime.setZone(shopTimeZone);
    if (!openingTime) {
        return false;
    }
    const shopOpeningTime = DateTime.fromFormat(openingTime, 'HH:mm', {
        zone: shopTimeZone,
    });
    if (shopOpeningTime !== null) {
        return userTimeInShopTZ.startOf('minute') < shopOpeningTime.startOf('minute');
    }
    return false;
};
export const isShopEnabled = ({ openingTime, closingTime, userCurrentDateTime, shopTimeZone, }) => {
    const userTimeInShopTZ = userCurrentDateTime.setZone(shopTimeZone);
    if (openingTime !== null && closingTime !== null) {
        const beginningTime = DateTime.fromFormat(openingTime, 'HH:mm', {
            zone: shopTimeZone,
        });
        const endTime = DateTime.fromFormat(closingTime, 'HH:mm', {
            zone: shopTimeZone,
        });
        if (userTimeInShopTZ.startOf('minute') < beginningTime.startOf('minute')) {
            return true;
        }
        return userTimeInShopTZ.startOf('minute') <= endTime.startOf('minute');
    }
};
export const getShopAvailabilityStatus = ({ openingTime, closingTime, userCurrentDateTime, shopTimeZone, }) => {
    const userTimeInShopTZ = userCurrentDateTime.setZone(shopTimeZone);
    if (openingTime !== null && closingTime !== null) {
        const beginningTime = DateTime.fromFormat(openingTime, 'HH:mm', {
            zone: shopTimeZone,
        });
        const endTime = DateTime.fromFormat(closingTime, 'HH:mm', {
            zone: shopTimeZone,
        });
        if (userTimeInShopTZ.startOf('minute') < beginningTime.startOf('minute')) {
            return ShopAvailabilityStatus.STILL_NOT_OPEN;
        }
        else if (userTimeInShopTZ.startOf('minute') > endTime.startOf('minute')) {
            return ShopAvailabilityStatus.CLOSED;
        }
        else {
            return ShopAvailabilityStatus.OPEN_NOW;
        }
    }
};
export const checkWorkingHours = ({ openingTime, closingTime }) => {
    return openingTime && closingTime && openingTime !== '' && closingTime !== '';
};
export const calculateNewShopWorkingHoursFields = ({ shop, pickupRules, tenantData, }) => {
    const userCurrentDateTime = DateTime.now().setZone(shop.shopTimeZone);
    if (userCurrentDateTime.toFormat('HH:mm:ss') < shop.closingTime) {
        return {
            enabled: shop.enabled,
            closingTime: shop.closingTime,
            openingTime: shop.openingTime,
            isOpeningLaterToday: shop.isOpeningLaterToday,
            openingTimeString: shop.availabilityStatus,
            checkWorkingHours: shop.checkWorkingHours,
        };
    }
    const timeRangeWithEnabled = getTimeRange({
        shop,
        userCurrentDateTime,
        pickupRules,
        tenantData,
        useShopOpeningHours: true,
    });
    const openingTimeString = getShopAvailabilityStatus({
        userCurrentDateTime,
        openingTime: timeRangeWithEnabled.openingTime,
        closingTime: timeRangeWithEnabled.closingTime,
        shopTimeZone: shop.shopTimeZone,
    });
    const enabled = isShopEnabled({
        userCurrentDateTime,
        openingTime: timeRangeWithEnabled.openingTime,
        closingTime: timeRangeWithEnabled.closingTime,
        shopTimeZone: shop.shopTimeZone,
    });
    return Object.assign({ isOpeningLaterToday: isOpeningLaterToday({
            userCurrentDateTime,
            openingTime: timeRangeWithEnabled.openingTime,
            shopTimeZone: shop.shopTimeZone,
        }), checkWorkingHours: checkWorkingHours({
            openingTime: timeRangeWithEnabled.openingTime,
            closingTime: timeRangeWithEnabled.closingTime,
        }), openingTimeString,
        enabled }, timeRangeWithEnabled);
};
export const generatePickupRulesFromShopOpeningHours = ({ useShopOpeningHoursByWeekday = false, shopData, dateParts, }) => {
    var _a;
    if (!useShopOpeningHoursByWeekday) {
        return [];
    }
    check.string.orFail(shopData.openingTime, 'shopData.openingTime');
    check.string.orFail(shopData.closingTime, 'shopData.closingTime');
    if (!shopData.openingHoursByWeekDay) {
        return [{ pickupRanges: [{ pickupRangeFrom: shopData.openingTime, pickupRangeTo: shopData.closingTime }] }];
    }
    const dateString = `${dateParts.year}-${(dateParts.month + '').padStart(2, '0')}-${(dateParts.day + '').padStart(2, '0')}`;
    const matchingDateRule = (_a = shopData.openingHoursByWeekDay.dateRules) === null || _a === void 0 ? void 0 : _a.find((rule) => rule.date === dateString);
    if (matchingDateRule && !matchingDateRule.closed) {
        return {
            pickupRanges: matchingDateRule.openingRanges.map((range) => ({
                pickupRangeFrom: range.opens,
                pickupRangeTo: range.closes,
            })),
        };
    }
    const weekDayRule = shopData.openingHoursByWeekDay.weekdayRules.find((rule) => dateParts.weekDay === WeekdayToTimeBoxWeekday[rule.weekday]);
    if (weekDayRule && !weekDayRule.closed) {
        return {
            pickupRanges: weekDayRule.openingRanges.map((range) => ({
                pickupRangeFrom: range.opens,
                pickupRangeTo: range.closes,
            })),
        };
    }
    return [];
};
export const isClosedAccordingToOpeningHours = ({ useShopOpeningHoursByWeekday = false, shopData, dateParts, }) => {
    var _a;
    if (!useShopOpeningHoursByWeekday || !shopData.openingHoursByWeekDay) {
        return false;
    }
    const dateString = `${dateParts.year}-${(dateParts.month + '').padStart(2, '0')}-${(dateParts.day + '').padStart(2, '0')}`;
    const matchingDateRule = (_a = shopData.openingHoursByWeekDay.dateRules) === null || _a === void 0 ? void 0 : _a.find((rule) => rule.date === dateString);
    if (matchingDateRule && matchingDateRule.closed) {
        return true;
    }
    const weekDayRule = shopData.openingHoursByWeekDay.weekdayRules.find((rule) => dateParts.weekDay === WeekdayToTimeBoxWeekday[rule.weekday]);
    if (weekDayRule && weekDayRule.closed) {
        return true;
    }
    return false;
};
